import { isFuture, parse } from 'date-fns'
import { fromJS, List, Map } from 'immutable'
import { isEmpty } from 'lodash-es'
import { createSelector } from 'reselect'

import {
  EligibilityType,
  LYRIANS_CUSTOMER_NAMES,
  PROGRAM_MEMBERSHIP_TYPES,
  SESSION_COUNT_STARTS_ON_FORMAT,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'
import { ProgramCustomerPropertyId } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { DEFAULT_PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { ImmutableTypedMap } from '@lyrahealth-inc/shared-app-logic/src/features/common/types/Immutable'
import { getCurrentYear } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { isCustomerSoleraCoaching } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/customerUtils'
import { parseMFAMethods } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/mfaUtils'
import { isDBTUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import {
  getCustomerConfiguration,
  getIsAnyCustomerProgramEAPCoaching,
  getIsAnyCustomerProgramEAPMeds,
  getIsAnyCustomerProgramEAPTherapy,
  getIsAnyCustomerProgramHPIMeds,
  getIsAnyCustomerProgramHPITherapy,
} from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/utils'
import { PROGRAMS } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { isHPIScreenDisabled } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { isPersonifyBenefitPartner } from '@lyrahealth-inc/shared-app-logic/src/features/registration/personifySso'
import {
  convertTriageTreatmentOptionToProgram,
  getProgramConfigByProvider,
} from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import {
  CustomerCopy,
  DEFAULT_MICROSITE_CONFIG,
  DEFAULT_PASSWORD_POLICY,
} from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'
import {
  Customer,
  CUSTOMER_TYPE,
  CustomerPropertiesMap,
  EAPAndHPIProgramSupport,
  YearProgramCoverage,
} from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { getCustomerCopy as getCustomerCopyHelper } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/customerCopy/getCustomerCopy'

import {
  areAllCustomerProgramsBHBExclusive,
  areAllCustomerProgramsEAPExclusive,
} from '../../common/constants/healthPlanConstants'
import {
  getHealthPlanEligibilityType,
  getHealthPlanPaymentCardLast4,
} from '../../features/healthPlan/data/healthPlanSelectors'
import {
  getOnboardProviderProfileData,
  getOnboardSelectedTreatmentOption,
} from '../../features/onboard/data/onboardSelectors'
import {
  getIsDBTTeensEnabled,
  getShouldShowWalmartHPICostTransparency,
  getShouldSurfaceNPEvalHPIScreen,
} from '../featureFlags/featureFlagSelectors'
import { getIsCustomerDynamicSessionLimitEnabled } from '../shared/sharedSelectors'

export const getCustomer = (state: $TSFixMe) => state.get('customer', Map())
export const getIsCustomerLoaded = createSelector(getCustomer, (customer) => Boolean(customer.get('isLoaded')))
export const getCustomerPropertiesMap = (state: $TSFixMe) => state.get('customerProperties', Map())

export const getBenefitPartner = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => customerPropertiesMap?.get('benefitPartner'),
)

export const getIsPersonifyBenefitPartner = createSelector(getBenefitPartner, (benefitPartner) =>
  isPersonifyBenefitPartner(benefitPartner),
)

const getTreatmentRecommendation = (state: $TSFixMe) => getOnboardSelectedTreatmentOption(state)

// If you need to use the customer's name in text to be displayed, use
// getCustomerInfo below to get the customer's
// name with proper casing. getCustomerName returns a lowercased name.
export const getCustomerName = createSelector(
  getCustomer,
  (customer: ImmutableTypedMap<Customer>) => customer?.get('name', '') ?? '',
)

export const getHealthPlan = (state: $TSFixMe) => state.get('healthPlan', Map())

export const getHPAndInfractionCardReminderType = createSelector(getHealthPlan, (healthPlan: any) =>
  (healthPlan as $TSFixMe)?.get('HPAndInfractionCardReminderType'),
)

export const getReceiveHealthPlanPrompt = createSelector(getHealthPlan, (healthPlan: any) => {
  return (healthPlan as $TSFixMe)?.get('receiveHealthPlanPrompt')
})

export const getUnresolvedInfractions = createSelector(getHealthPlan, (healthPlan: any) =>
  (healthPlan as $TSFixMe)?.get('unresolvedInfractions'),
)

export const getCustomerId = createSelector(getCustomer, (customer: Customer) => (customer as $TSFixMe)?.get('id'))

export const getCustomerInfo = createSelector(getCustomer, (customer: Customer) => (customer as $TSFixMe)?.get('info'))

export const getCustomerHealthPlansList = createSelector(getCustomer, (customerSelector) => {
  return customerSelector.get('healthPlansList')
})

export const getCustomerNonIntegratedHealthPlansList = createSelector(getCustomer, (customerSelector) => {
  return customerSelector.get('nonIntegratedHealthPlansList')
})

export const getCustomerProductType = createSelector(getCustomer, (customerSelector) => {
  return customerSelector.get('productType') ?? DEFAULT_PRODUCT_TYPE
})

export const getCustomerSubdomain = createSelector(getCustomer, (customerSelector) => {
  return customerSelector.get('subdomain')
})

export const getProgramCoverageBreakdown = createSelector(getCustomer, (customerSelector) => {
  const programCoverageBreakDown = customerSelector.getIn(['programCoverageBreakdown', getCurrentYear()])
  return programCoverageBreakDown ?? Map()
})

export const getIsCustomerHPIExclusive = createSelector(getProgramCoverageBreakdown, (customerPrograms: object) => {
  return areAllCustomerProgramsBHBExclusive(fromJS(customerPrograms))
})

/**
 * Gets the customerType property from the customer object
 * @returns The customerType value ('EAP', 'HPI', or 'EAP_HPI')
 */
export const getCustomerType = createSelector(getCustomer, (customer: ImmutableTypedMap<Customer>) => {
  return customer?.get('customerType') as CUSTOMER_TYPE | undefined
})

/**
 * Checks if customer is HPI exclusive using the customerType property from currentuser API
 */
export const getIsCustomerHPIExclusiveByType = createSelector(getCustomerType, (customerType: string | undefined) => {
  return customerType === CUSTOMER_TYPE.HPI
})

/**
 * Checks if customer is EAP exclusive using the customerType property from currentuser API
 */
export const getIsCustomerEAPExclusiveByType = createSelector(getCustomerType, (customerType: string | undefined) => {
  return customerType === CUSTOMER_TYPE.EAP
})

export const getIsCustomerEAPExclusive = createSelector(getProgramCoverageBreakdown, (customerPrograms: object) => {
  return areAllCustomerProgramsEAPExclusive(fromJS(customerPrograms))
})

/**
 * Checks if customer is EAP+HPI using the customerType property from currentuser API
 */
export const getIsCustomerEAPAndHPISupportedByType = createSelector(
  getCustomerType,
  (customerType: string | undefined) => {
    return customerType === CUSTOMER_TYPE.EAP_HPI
  },
)

const convertMapToYearProgramCoverage = (customerPrograms: $TSFixMe): YearProgramCoverage => {
  const coverageObject: YearProgramCoverage = {}

  customerPrograms.forEach((value: any, key: ProgramCustomerPropertyId) => {
    coverageObject[key] = Object.fromEntries(value) as EAPAndHPIProgramSupport
  })

  return coverageObject
}

export const getIsCustomerHPIMeds = createSelector(getProgramCoverageBreakdown, (customerPrograms: object) => {
  const yearProgramCoverages = convertMapToYearProgramCoverage(fromJS(customerPrograms))
  return getIsAnyCustomerProgramHPIMeds(yearProgramCoverages)
})

export const getIsCustomerEAPMeds = createSelector(getProgramCoverageBreakdown, (customerPrograms: object) => {
  const yearProgramCoverages = convertMapToYearProgramCoverage(fromJS(customerPrograms))
  return getIsAnyCustomerProgramEAPMeds(yearProgramCoverages)
})

export const getIsCustomerProgramEAPCoaching = createSelector(
  getProgramCoverageBreakdown,
  (customerPrograms: object) => {
    const yearProgramCoverages = convertMapToYearProgramCoverage(fromJS(customerPrograms))
    return getIsAnyCustomerProgramEAPCoaching(yearProgramCoverages)
  },
)

export const getIsCustomerProgramHPITherapy = createSelector(
  getProgramCoverageBreakdown,
  (customerPrograms: object) => {
    const yearProgramCoverages = convertMapToYearProgramCoverage(fromJS(customerPrograms))
    return getIsAnyCustomerProgramHPITherapy(yearProgramCoverages)
  },
)

export const getIsCustomerProgramEAPTherapy = createSelector(
  getProgramCoverageBreakdown,
  (customerPrograms: object) => {
    const yearProgramCoverages = convertMapToYearProgramCoverage(fromJS(customerPrograms))
    return getIsAnyCustomerProgramEAPTherapy(yearProgramCoverages)
  },
)

export const getIsCustomerEAPAndHPISupported = createSelector(
  getIsCustomerHPIExclusive,
  getIsCustomerEAPExclusive,
  (isCustomerHPIExclusive, isCustomerEAPExclusive) => {
    return !isCustomerHPIExclusive && !isCustomerEAPExclusive
  },
)

export const getHasHPIMedsWithMixedProgramSupport = createSelector(
  getIsCustomerHPIExclusive,
  getIsCustomerEAPExclusive,
  getIsCustomerHPIMeds,
  getProgramCoverageBreakdown,
  (isCustomerHPIExclusive, isCustomerEAPExclusive, isCustomerHPIMeds, customerPrograms) => {
    // Check if customer has HPI-only medication management AND offers other programs as EAP+HPI or EAP only
    const hasHPIMedsWithOtherEAPPrograms =
      isCustomerHPIMeds && !areAllCustomerProgramsBHBExclusive(fromJS(customerPrograms))

    return hasHPIMedsWithOtherEAPPrograms
  },
)

export const getCustomerSupportedPrograms = createSelector(getCustomer, (customerSelector) => {
  const supportedPrograms = customerSelector.get('supportedPrograms')
  if (!supportedPrograms) {
    return fromJS([])
  }
  return supportedPrograms
})

export const getIsAUDSupported = createSelector(getCustomerSupportedPrograms, (supportedPrograms) => {
  return supportedPrograms.includes(PROGRAMS.AlcoholUseDisorderTherapy.customerPropertyId)
})

export const getIsMedsManagementSupported = createSelector(getCustomerSupportedPrograms, (supportedPrograms) => {
  return supportedPrograms.includes(PROGRAMS.MedicationManagement.customerPropertyId)
})

export const getIsDirectAccessMedsSupported = createSelector(getCustomerSupportedPrograms, (supportedPrograms) => {
  return supportedPrograms.includes(PROGRAMS.DIRECT_ACCESS_MEDS.customerPropertyId)
})

export const getIsDirectAccessTherapySupported = createSelector(getCustomerSupportedPrograms, (supportedPrograms) => {
  return supportedPrograms.includes(PROGRAMS.DIRECT_ACCESS_THERAPY.customerPropertyId)
})

export const getCustomerDependentTypes = createSelector(getCustomer, (customer) => customer?.get('dependentTypes'))

export const getCustomerCopy = createSelector(
  [
    getCustomerPropertiesMap,
    getCustomerName,
    (state: $TSFixMe) => getHealthPlanEligibilityType(state),
    getCustomerInfo,
    getIsCustomerDynamicSessionLimitEnabled,
    getIsDBTTeensEnabled,
  ],
  (
    customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>,
    customerName: string,
    eligibilityType: EligibilityType,
    customerDisplayName: string,
    isCustomerDynamicSessionLimitEnabled: boolean,
    isDBTTeensEnabled: boolean,
  ): CustomerCopy => {
    const customerCopy = getCustomerCopyHelper(
      customerName,
      customerPropertiesMap?.toJS(),
      eligibilityType,
      customerDisplayName,
      isCustomerDynamicSessionLimitEnabled,
      isDBTTeensEnabled,
    )
    return customerCopy
  },
)

export const getShouldDisplayCareTeamContactInfo = createSelector(
  getCustomerCopy,
  (copy) => copy?.shouldDisplayCareTeamContactInfo ?? true,
)

export const getLegalNotice = createSelector(getCustomerCopy, (copy) => copy?.legalNotice ?? '')

export const getGenerateEligibilityQuestionTitle = createSelector(
  [getCustomerCopy],
  (copy) => copy?.generateEligibilityQuestionTitle,
)

export const getShowEligibilityQuestionForInternationalRegistration = createSelector(
  [getCustomerCopy],
  (copy) => copy?.showEligibilityQuestionForInternationalRegistration ?? false,
)

export const getGenerateEligibilityDependentTitle = createSelector(
  [getCustomerCopy],
  (copy) => copy?.generateEligibilityDependentTitle,
)

export const getGenerateConfirmEligibilityFormSubHeaderText = createSelector(
  [getCustomerCopy],
  (copy) => copy?.generateConfirmEligibilityFormSubHeaderText,
)

export const getGenerateConfirmEligibilityFormDefaultError = createSelector(
  [getCustomerCopy],
  (copy) => copy?.generateConfirmEligibilityFormDefaultError,
)

export const getEligibilityQuestionSubtitle = createSelector(
  [getCustomerCopy],
  (copy) => copy?.eligibilityQuestionSubtitle,
)

export const getDependentButtonText = createSelector(getCustomerCopy, (copy) => copy?.dependentButtonText)

export const getDependentIneligibleText = createSelector(getCustomerCopy, (copy) => copy?.dependentIneligibleText)

export const getCustomEligibilityMetadata = createSelector(getCustomerCopy, (copy) => copy?.eligibilityMetadata)

export const getUserMinAgeRequirement = createSelector(getCustomerCopy, (copy) => copy?.userMinAgeRequirement)

export const getCustomUnderageErrorWidget = createSelector(getCustomerCopy, (copy) => copy?.customUnderageErrorWidget)

export const getDisableSubmitOnDependentSelection = createSelector(
  getCustomerCopy,
  (copy) => copy?.disableSubmitOnDependentSelection,
)

export const getIsExternalMarketplaceCustomer = createSelector(
  getCustomerCopy,
  (copy) => copy?.isExternalMarketplaceCustomer,
)

export const getShowCountrySelector = createSelector(getCustomerCopy, (copy) => copy?.showCountrySelector)

export const getCarelonProviderDirectoryUrl = createSelector(
  getCustomerCopy,
  (copy) => copy?.carelonProviderDirectoryUrl,
)

export const getGenerateEligibilityCheckSubmissionErrors = createSelector(
  [getCustomerCopy],
  (copy) => copy?.generateEligibilityCheckSubmissionErrors,
)

export const getShouldDisplayBCTProviderBenefits = createSelector(
  getCustomerCopy,
  (copy) => copy?.shouldDisplayBCTProviderBenefits ?? false,
)

const getLocationFinder = createSelector(getCustomerCopy, (copy) => copy?.locationFinder)

export const getLocationOnsiteFormat = createSelector(getLocationFinder, (copy) => copy?.onsiteFormat)

export const getLocationOnsiteDescription = createSelector(getLocationFinder, (copy) => copy?.onsiteDescription)

const getModalityQuestionnaireText = createSelector(getCustomerCopy, (copy) => copy?.modalityQuestionnaireText)

export const getSubpopulations = createSelector(getCustomerCopy, (copy) => copy?.subpopulations)

export const getSubpopulations_lctTeens = createSelector(getCustomerCopy, (copy) => copy?.subpopulations_lctTeens)

export const getCustomerRedirection = createSelector(getCustomerCopy, (copy) => copy?.customerRedirection)

export const getServicesCovered = createSelector(getCustomerCopy, (copy) => copy?.servicesCovered)

export const getServicesNotCovered = createSelector(getCustomerCopy, (copy) => copy?.servicesNotCovered)

export const getShouldUseCustomChildCare = createSelector(getCustomerCopy, (copy) => copy?.shouldUseCustomChildCare)

export const getModalityLabelAddendums = createSelector(
  getModalityQuestionnaireText,
  (copy) => copy?.modalityLabelAddendums,
)

export const getCostMessagingModalText = createSelector(getCustomerCopy, (copy) => copy?.costMessagingModalText)

export const getCoverageModalTitleText = createSelector(getCustomerCopy, (copy) => copy?.coverageModalTitleText)

export const getSessionsCoverageText = createSelector(getCustomerCopy, (copy) => copy?.sessionsCoverageText)

export const getHideChildSearchCTA = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    // @ts-expect-error TS(2345): Argument of type 'boolean' is not assignable to pa... Remove this comment to see the full error message
    customerPropertiesMap?.get('shouldHideChildSearchCTA', false) === 'true',
)

export const getIsMHEToolkitEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isMHEToolkitEnabled', 'false') === 'true',
)

export const getCustomerLaunchDate = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => customerPropertiesMap?.get('launchDate'),
)

export const getBeforeLaunch = createSelector(
  getCustomerPropertiesMap,
  (state: $TSFixMe) => state.getIn(['appGlobals', 'bypassComingSoonBeforeLaunch']),
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>, bypassComingSoon: boolean) => {
    if (bypassComingSoon) {
      return false
    }
    const launchDate = customerPropertiesMap?.get('launchDate')
    return launchDate ? isFuture(new Date(launchDate)) : false
  },
)

export const getCustomerApproveWithNoEligibilityCheck = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('approveWithNoEligibilityCheck', 'false') === 'true',
)

export const getIsCustomerOptedOutOfEligibilityCheck = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('optOutOfEligibilityCheck', 'false') === 'true',
)

export const getCustomerWorkLifeBenefits = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => customerPropertiesMap?.get('worklife', ''),
)

export const getCustomerWorkLifePhone = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => customerPropertiesMap?.get('worklifephone'),
)

export const getIsCustomerDependentTypesEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isDependentTypesEnabled', 'false') === 'true',
)

export const getShouldHideEthnicityField = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('hideEthnicityRegistrationQuestion', 'false') === 'true',
)

export const getShouldSeeCareAdvocateRequestForm = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('shouldSeeCareAdvocateRequestForm', 'false') === 'true',
)

export const getHideDependentField = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('hideDependentField', 'false') === 'true',
)

export const getBlockInPersonCareForDependents = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('blockInPersonCareForDependents', 'false') === 'true',
)

export const getIsMFARequiredForUsers = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isMFARequiredForUsers', 'false') === 'true',
)

export const getMFAMethods = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => {
    return parseMFAMethods(customerPropertiesMap?.get('mfaMethod'))
  },
)

export const getIsLiveChatEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>): boolean =>
    customerPropertiesMap?.get('enabledChat', 'false') === 'true',
)

export const getLiveMessagingEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('liveMessagingEnabled', 'false') === 'true',
)

export const getGuidedSelfCareEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('guidedSelfCareEnabled', 'false') === 'true',
)

export const getCustomerPhone = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => getCustomerPropertiesMap?.get('phone'),
)

/**
 * Returns the raw customer property numberOfSessions
 *
 * Use getUserEAPSessionLimit in userSelectors.ts to get the effective session limit for the current user
 */
export const getNumberOfSessions = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('numberOfSessions'),
)

export const getNumberOfSessionsByProgram = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => {
    const jsonStr = getCustomerPropertiesMap?.get('numberOfSessionsByProgram')
    if (jsonStr) {
      return JSON.parse(jsonStr) as SessionLimitByProgram
    } else {
      return undefined
    }
  },
)

export const getDisplayVisitsPerIssuePerYear = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('displayVisitsPerIssuePerYear'),
)

export const getDisplayVisitsPerIssuePerYearByProgram = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => {
    const jsonStr = getCustomerPropertiesMap?.get('displayVisitsPerIssuePerYearByProgram')
    if (jsonStr) {
      return JSON.parse(jsonStr) as SessionCountByProgram
    } else {
      return undefined
    }
  },
)

export const getGraphicContentGroup = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    // @ts-expect-error TS(2345): Argument of type '"graphicContentGroup"' is not as... Remove this comment to see the full error message
    getCustomerPropertiesMap?.get('graphicContentGroup', ''),
)

export const getCancellationLimit = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('cancellationLimit'),
)

export const getCancellationsIncludedInSessionLimits = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('cancellationsIncludedInSessionLimits', 'false') === 'true',
)

export const getFAQText = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => getCustomerPropertiesMap?.get('faqText'),
)

export const getBenefitsPortalLink = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('benefitsPortalLink'),
)

export const getLwMobileEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('lwMobileEnabled', 'false') === 'true',
)

export const getIsLyraNicotineQuitlineEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('lyraNicotineQuitlineEnabled', 'false') === 'true',
)

export const getIsDataShareAgreementAccepted = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('dataShareAgreementAccepted', 'false') === 'true',
)

export const getSessionCountStartsOn = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('sessionCountStartsOn'),
)

export const getSessionCountStartsOnDate = createSelector(getSessionCountStartsOn, (sessionCountStartsOn) =>
  parse(sessionCountStartsOn || 'Jan-01', SESSION_COUNT_STARTS_ON_FORMAT, new Date()),
)

export const getHealthPlanExtensionEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('healthPlanExtensionEnabled', 'false') === 'true',
)

export const getIsClientHPI = createSelector(
  getHealthPlanExtensionEnabled,
  getHealthPlanPaymentCardLast4,
  (healthPlanExtensionEnabled: boolean, costSharingCardLast4: string | null) =>
    healthPlanExtensionEnabled || !isEmpty(costSharingCardLast4),
)

export const getDepartingRedirectURL = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    // @ts-expect-error TS(2345): Argument of type '"departingRedirectUrl"' is not a... Remove this comment to see the full error message
    customerPropertiesMap?.get('departingRedirectUrl'),
)

export const getIsCustomerCareTeam = createSelector(getCustomerName, (name) => name === 'care')

// checks if the customer is Lyra for lyrians which includes Lyra Health and Lyra Clinical
export const getIsCustomerLyra = createSelector(getCustomerName, (name) =>
  Object.values(LYRIANS_CUSTOMER_NAMES).includes(name),
)

export const getIsCustomerLyraBootcamp = createSelector(getCustomerName, (name) => name === 'lyrabootcamp')

export const getSelectedProgram = createSelector(
  (state: $TSFixMe) => getOnboardProviderProfileData(state),
  getTreatmentRecommendation,
  (provider: $TSFixMe, selectedTreatmentOption: $TSFixMe) =>
    (convertTriageTreatmentOptionToProgram(selectedTreatmentOption) ?? getProgramConfigByProvider(provider.toJS()))
      ?.customerPropertyId,
)

export const getProgramConfiguration = createSelector(
  getSelectedProgram,
  getProgramCoverageBreakdown,
  (selectedProgram, customerPrograms) => customerPrograms.get(selectedProgram),
)

export const getIsCustomerProgramHPIEligible = createSelector(
  getProgramConfiguration,
  (programConfiguration) =>
    programConfiguration?.get('bhbSupported') || programConfiguration?.get('specialtyBhbSupported'),
)

export const getIsCustomerProgramHPIExclusive = createSelector(
  getProgramConfiguration,
  getIsCustomerProgramHPIEligible,
  (programConfiguration, isCustomerProgramHPIEligible) =>
    !!(isCustomerProgramHPIEligible && !programConfiguration?.get('eapSupported')),
)

export const getCustomerConfigurationValue = createSelector(
  getProgramCoverageBreakdown,
  getHealthPlanExtensionEnabled,
  (customerPrograms, healthPlanExtensionEnabled) => {
    return getCustomerConfiguration(customerPrograms?.toJS(), healthPlanExtensionEnabled)
  },
)

export const getLibraryDisabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('libraryDisabled', 'false') === 'true',
)

export const getEssentialsEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('libraryDisabled', 'false') !== 'true',
)

export const getDisableSelfCareTopics = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('disableSelfCareTopics'),
)

export const getIsCustomerInternational = createSelector(
  getCustomer,
  (customer) => customer?.get('internationalSupport') ?? false,
)

export const getIsHealthPlanDirect = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isHealthPlanDirect', 'false') === 'true',
)

export const getProgramMembershipType = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('programMembershipType') ?? PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE,
)

export const getICASUsername = createSelector(
  getCustomerPropertiesMap,
  getCustomerName,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>, customerName) =>
    customerPropertiesMap?.get('icasUsername', customerName),
)

export const getEmployeeRegistrationNomenclature = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('employeeRegistrationNomenclature'),
)

export const getDependentRegistrationNomenclature = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('dependentRegistrationNomenclature'),
)

export const getEligibilityTitleValues = createSelector(
  [getEmployeeRegistrationNomenclature, getCustomerInfo],
  (nomenclature, customerInfo) => {
    return {
      employeeType: nomenclature || 'other',
      hasCustomerName: customerInfo ? 'yes' : 'other',
      customerName: customerInfo,
    }
  },
)

export const getOktaPasswordPolicy = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => {
    const oktaPasswordPolicy = customerPropertiesMap?.get('oktaPasswordPolicy')
    return oktaPasswordPolicy ? JSON.parse(oktaPasswordPolicy) : DEFAULT_PASSWORD_POLICY
  },
)

export const getIsAppleSSODisabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isAppleSSODisabled', 'false') === 'true',
)

export const getIsGoogleSSODisabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isGoogleSSODisabled', 'false') === 'true',
)

export const getAccountLockedText = createSelector(getCustomerCopy, (copy) => copy?.accountLockedText)
export const getPasswordResetFailedText = createSelector(getCustomerCopy, (copy) => copy?.passwordResetFailedText)

export const getHideWorkLifeServicesForInternationalUser = createSelector(
  getCustomerCopy,
  (copy) => copy?.hideWorkLifeServicesForInternationalUser,
)

export const getCountryISOCodesToHideWorkLifeServicesFor = createSelector(
  getCustomerCopy,
  (copy) => copy?.countryISOCodesToHideWorkLifeServicesFor,
)

export const getHasBlockingEligibilityCheck = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('hasBlockingEligibilityCheck', 'false') === 'true',
)

export const getCheckAdultDependentEligibilityUsingDependentInfo = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('checkAdultDependentEligibilityUsingDependentInfo', 'false') === 'true',
)

export const getCheckChildDependentEligibilityUsingDependentInfo = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('checkChildDependentEligibilityUsingDependentInfo', 'false') === 'true',
)

export const getInfractionMessagingDisabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('infractionMessagingDisabled', 'false') === 'true',
)

export const getDefaultDirectCareNavigatorBookingLink = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('defaultDirectCareNavigatorBookingLink'),
)

export const getIsLibraryEventsDisabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    !customerPropertiesMap?.has('isLibraryEventsDisabled')
      ? false
      : customerPropertiesMap?.get('isLibraryEventsDisabled', 'false') === 'true',
)

export const getIsICASHubLinkDisabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isICASHubLinkDisabled', 'false') === 'true',
)

export const getMicrositeConfig = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => {
    const micrositeConfig = customerPropertiesMap?.get('micrositeConfig')
    return micrositeConfig ? { ...DEFAULT_MICROSITE_CONFIG, ...JSON.parse(micrositeConfig) } : DEFAULT_MICROSITE_CONFIG
  },
)

export const isWalmartCustomer = (state: $TSFixMe) => getCustomerName(state) === CustomerName.WALMART

export const getIsCoachingEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('stressManagement') === 'true',
)

export const getIsCoachingForParentsEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('coachingForParents') === 'true',
)

export const getIsDAMedsEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('considerMeds') === 'true',
)

export const getIsLCTherapyEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('considerLyraTherapy') === 'true',
)

export const getIsDirectAccessTherapyEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('directAccessTherapy') === 'true',
)

/** Program is also known as mental health evaluations (MHE) */
export const getIsClinicalLeaveEvaluationEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('clinicalLeaveEvaluation') === 'true',
)

export const getIsTherapyEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('considerLyraTherapy') === 'true' ||
    customerPropertiesMap?.get('directAccessTherapy') === 'true',
)

export const getIsBlendedCareMedsAvailable = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('blendedCareMeds') === 'true',
)

export const getIsComplexCareEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isComplexCareEnabled') === 'true',
)

export const getIsHPIRequiredForAppointmentBookingGetter = createSelector(
  [
    getIsCustomerProgramHPIEligible,
    getIsCustomerProgramHPIExclusive,
    getIsCustomerHPIExclusive,
    getSelectedProgram,
    getShouldSurfaceNPEvalHPIScreen,
  ],
  (
    isCustomerProgramHPIEligible,
    isCustomerProgramHPIExclusive,
    isCustomerHPIExclusive,
    selectedProgram,
    shouldSurfaceNPEvalHPIScreen,
  ) =>
    (isEAPSessionCountExhausted: boolean) => {
      if (isHPIScreenDisabled(selectedProgram, shouldSurfaceNPEvalHPIScreen)) {
        return false
      }
      return (
        (isCustomerProgramHPIEligible && isEAPSessionCountExhausted) ||
        isCustomerProgramHPIExclusive ||
        isCustomerHPIExclusive
      )
    },
)

export const getIsHPIRequiredForProgramGetter = createSelector(
  [getProgramCoverageBreakdown, getIsCustomerHPIExclusive, getShouldSurfaceNPEvalHPIScreen],
  (programCoverageBreakdown, isCustomerHPIExclusive, shouldSurfaceNPEvalHPIScreen) =>
    (programName: ProgramCustomerPropertyId) =>
    (isEAPSessionCountExhausted: boolean) => {
      if (!programName || !programCoverageBreakdown || isHPIScreenDisabled(programName, shouldSurfaceNPEvalHPIScreen)) {
        return false
      }

      const programConfig = programCoverageBreakdown.get(programName)
      if (!programConfig) {
        return false
      }

      const isCustomerProgramHPIEligible =
        programConfig.get('bhbSupported') || programConfig.get('specialtyBhbSupported')
      const isCustomerProgramHPIExclusive = isCustomerProgramHPIEligible && !programConfig.get('eapSupported')

      return (
        (isCustomerProgramHPIEligible && isEAPSessionCountExhausted) ||
        isCustomerProgramHPIExclusive ||
        isCustomerHPIExclusive
      )
    },
)

/**
 * @deprecated. use `getCustomerCopy` instead
 */
const getCustomerCopyMap = createSelector(
  getCustomer,
  (customer: Customer) => (customer as $TSFixMe)?.get('copies') as Map<string, unknown>,
)

// ===== START ==== below are selectors selecting for old customer copies which are used in web only

export const getFAQQuestions = createSelector(getCustomerCopyMap, (copy) => copy?.get('faqQuestions', List()))

export const getHideExtraHeaderOptions = createSelector(getCustomerCopyMap, (copy) =>
  copy?.get('hideExtraHeaderOptions', false),
)

const getMicrositeAndElsewhereTopBannerCopy = createSelector(getCustomerCopyMap, (copy) =>
  copy?.get('micrositeAndElsewhereTopBanner', Map()),
)

export const getMicrositeAndElsewhereTopBannerProps = createSelector(getCustomerCopyMap, (copy) =>
  copy?.get('micrositeAndElsewhereTopBannerProps', Map()),
)

export const getMicrositeAndElsewhereTopBanners = createSelector(getMicrositeAndElsewhereTopBannerCopy, (copy) =>
  (copy as $TSFixMe)?.get('banners', List()),
)

export const getGenerateEmployeeEligibilityError = createSelector(
  getCustomerCopy,
  (copy) => copy?.generateEmployeeEligibilityError,
)

export const getGenerateRegistrationSubHeaderText = createSelector(
  [getCustomerCopy],
  (copy) => copy?.generateRegistrationFormSubHeaderText,
)

export const getScheduleResourceCoordinatorCallLink = createSelector(
  [getCustomerCopy],
  (copy) => copy?.scheduleResourceCoordinatorCallLink,
)

// ==== END ==== above are selectors selecting for old customer copies which are used in web only

export const getIsSecondaryHealthPlanCollectionEnabled = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('isSecondaryHealthPlanCollectionEnabled', 'false') === 'true',
)

export const getIsLyraSocialCareEnabled = createSelector(
  getCustomerPropertiesMap,
  (getCustomerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    getCustomerPropertiesMap?.get('lyraSocialCareEnabled', 'false') === 'true',
)

export const getIsPhoneNumberRequiredAtRegistration = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isPhoneNumberRequiredAtRegistration') === 'true',
)

export const getIsASDCoordinationEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) => {
    return customerPropertiesMap?.get('isASDCoordinationEnabled') === 'true'
  },
)

export const getIsOneMicrositeMemberSubpopulationEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isOneMicrositeMemberSubpopulationEnabled') === 'true',
)

export const getGenAiOptOut = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('genAiOptOut') === 'true',
)

export const getIsNeuropsychologicalEvaluationsEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('neuropsychologicalEvaluations') === 'true',
)

export const getIsCaregiverTrainingForNeuroEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('caregiverTrainingForNeuro') === 'true',
)

export const getIsNeurodiversitySkillsCoachingEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('neurodiversitySkillsCoaching') === 'true',
)

export const getIsPediatricsCOEProgramsEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('dialecticalBehaviorTherapyTeens') === 'true' &&
    customerPropertiesMap?.get('pediatricVirtualIntensiveOutpatientProgram') === 'true' &&
    customerPropertiesMap?.get('behavioralHealthUrgentCare') === 'true',
)

export const getIsPediatricvIOPOfferedThroughBend = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isPediatricvIOPOfferedThroughBend') === 'true',
)

export const getIsNeurodiversityCareAdvocacyEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('neurodiversityCareAdvocacy') === 'true',
)

export const getIsNeurodiversityNavigationContactPageEnabled = createSelector(
  [getIsASDCoordinationEnabled, getIsNeurodiversityCareAdvocacyEnabled],
  (isASDCoordinationEnabled, isNeurodiversityCareAdvocacyEnabled) =>
    isASDCoordinationEnabled || isNeurodiversityCareAdvocacyEnabled,
)

export const getIsWalmartHPICostTransparencyEnabled = createSelector(
  [getShouldShowWalmartHPICostTransparency, isWalmartCustomer],
  (isFlagEnabled, isWalmart) => isFlagEnabled === true && isWalmart,
)

export const getIsCustomerCoachingOnly = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('considerLyraTherapy') !== 'true' &&
    customerPropertiesMap?.get('directAccessTherapy') !== 'true' &&
    customerPropertiesMap?.get('blendedCareMeds') !== 'true' &&
    customerPropertiesMap?.get('considerMeds') !== 'true' &&
    customerPropertiesMap?.get('alcoholUseDisorderTherapy') !== 'true' &&
    customerPropertiesMap?.get('stressManagement') === 'true',
)

export const getIsCustomerSoleraCoaching = createSelector(getCustomerName, (name) => isCustomerSoleraCoaching(name))

export const getIsSelfPayEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('selfPayEnabled', 'false') === 'true',
)

export const getIsDBTCustomer = createSelector(getCustomerName, (name) => isDBTUser(name))

export const getIsAirlineCompany = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('isAirlineCompany', 'false') === 'true',
)

export const getIsZeroDollarCostShareEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('zeroDollarCostShare', 'false') === 'true',
)

export const getIsHybridLoginEnabled = createSelector(
  getCustomerPropertiesMap,
  (customerPropertiesMap: ImmutableTypedMap<CustomerPropertiesMap>) =>
    customerPropertiesMap?.get('hybridLoginEnabled', 'false') === 'true',
)
